import React from 'react';
import { observer } from 'mobx-react-lite';
import { useShelfRFQProductTemplatesStore } from 'store/pages/rfq/rfq-template/shelf-rfq-product-templates/pageStore';
import { generateTemplateJson } from 'api/operations/productServiceV2';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { CloudUpload, Delete, Download } from '@insurancemenu/imenu-components-lib/dist/icons';
import { Button, colors } from '@insurancemenu/imenu-components-lib';
import { SHELF_PRODUCT_TEMPLATES_ROW_TEST_IDS } from '../../constants';
import {
  ButtonsWrapperStyled,
  InternalNameStyled,
  TableCellStyled,
  TableRowStyled,
  UploadButtonStyled,
} from './styles';

interface Props {
  productId: string;
  templateId?: string;
  productLineName: string;
  shelfProductName: string;
  internalTemplateName?: string;
  externalTemplateName?: string;
}

const TableRow: React.FC<Props> = observer(
  ({ productId, templateId, productLineName, shelfProductName, internalTemplateName, externalTemplateName }) => {
    const { uploadJsonModalDelegate, deleteTemplateModalDelegate } = useShelfRFQProductTemplatesStore();

    const handleDownload = async (): Promise<void> => {
      if (!templateId) return;

      try {
        const link = await generateTemplateJson(templateId);
        if (link) {
          downloadByUrl(link);
        }
      } catch {
        toastManager.addToast('Something went wrong during file download', ToastColorTypes.WARNING);
      }
    };

    const onOpenUploadJsonModal = (): void => {
      uploadJsonModalDelegate.open(productId, shelfProductName);
    };

    const onOpenDeleteTemplateModal = (): void => {
      if (!templateId) return;
      deleteTemplateModalDelegate.open(templateId, shelfProductName);
    };

    return (
      <TableRowStyled key={productId} data-test-id={SHELF_PRODUCT_TEMPLATES_ROW_TEST_IDS.ROW}>
        <TableCellStyled width="4rem">{productId}</TableCellStyled>
        <TableCellStyled width="10rem">{shelfProductName}</TableCellStyled>
        <TableCellStyled width="6rem">{productLineName}</TableCellStyled>
        <TableCellStyled width="8rem">
          <InternalNameStyled>{internalTemplateName}</InternalNameStyled>
        </TableCellStyled>
        <TableCellStyled width="8rem">{externalTemplateName}</TableCellStyled>
        <TableCellStyled width="6rem">
          {templateId && (
            <ButtonsWrapperStyled>
              <Button
                type="icon"
                color="primary"
                disabled={!templateId}
                onClick={handleDownload}
                iconSize="2.8rem"
                data-test-id={SHELF_PRODUCT_TEMPLATES_ROW_TEST_IDS.DOWNLOAD_JSON_BTN}
              >
                <Download width="3rem" height="3rem" />
              </Button>
              <Button
                type="icon"
                color="custom"
                customColor={colors.red[600]}
                disabled={!templateId}
                onClick={onOpenDeleteTemplateModal}
                iconSize="2.8rem"
                data-test-id={SHELF_PRODUCT_TEMPLATES_ROW_TEST_IDS.DELETE_JSON_BTN}
              >
                <Delete />
              </Button>
            </ButtonsWrapperStyled>
          )}
        </TableCellStyled>
        <TableCellStyled width="7rem">
          <UploadButtonStyled
            color="primary"
            type="text"
            uppercase
            leftIconProp={<CloudUpload />}
            onClick={onOpenUploadJsonModal}
            data-test-id={SHELF_PRODUCT_TEMPLATES_ROW_TEST_IDS.UPLOAD_JSON_BTN}
          >
            Upload JSON
          </UploadButtonStyled>
        </TableCellStyled>
      </TableRowStyled>
    );
  },
);

export default TableRow;
